import { useState } from 'react';
import { Button } from '../../Components/UI/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../Components/UI/dropdown-menu';
import { ChevronDown, Plus, } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getTransactionTypesArr } from '../../Store/Reducers';
import { currentUserHasPermission, getTransactionIcon, getTransactionIconColorClass } from '../../Utils/Utilities';
import MobileFullScreenDropdown from '../../Components/Common/MobileDropdown';
import useIsMobile from '../../Hooks/useIsMobile';

const TransactionDropdown = ({
  className = "",
  onTransactionTypeSelect,
  connectionType = null,
  externalOpen = false,
  onExternalOpenChange = null,
  title = "Enter Transaction",
  minimalIcon = false,
  dropdownAlign = "end",
  showDefaultBox = false,
  showDefaultCustomBox = false
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const allTransactionTypes = useSelector(getTransactionTypesArr);
  const isMobile = useIsMobile();

  // Use external control if provided, otherwise use internal state
  const isOpen = onExternalOpenChange ? externalOpen : internalOpen;
  const setIsOpen = onExternalOpenChange ? onExternalOpenChange : setInternalOpen;

  // Check if user can create transactions (XnActions 1 or 3)
  const xnActions = currentUserHasPermission('XnActions');
  const canCreateXn = xnActions === 1 || xnActions === 3;

  // Return null if user doesn't have permission to create transactions
  if (!canCreateXn) {
    return null;
  }

  // Filter by connection type if provided
  const getFilteredTransactionTypes = (transactionTypes, connType) => {
    if (!connType || !transactionTypes) return transactionTypes || [];

    // For Customer connections (C), show Sales transactions (S)
    if (connType === 'C') return transactionTypes.filter(t => t.Typ === 'S');
    // For Supplier connections (S), show Purchase transactions (P)
    if (connType === 'S') return transactionTypes.filter(t => t.Typ === 'P');

    return transactionTypes;
  };

  // Get display name - prefer LblF over LblS, with fallback
  const getDisplayName = (transactionType) => {
    if (transactionType.LblF && transactionType.LblF.trim() !== '') {
      return transactionType.LblF;
    }
    if (transactionType.LblS && transactionType.LblS.trim() !== '') {
      return transactionType.LblS;
    }
    return `Transaction ${transactionType.XnTID}`;
  };

  const filteredTransactionTypes = getFilteredTransactionTypes(allTransactionTypes, connectionType);

  // Show disabled button if no transaction types available at all
  if (!allTransactionTypes || allTransactionTypes.length === 0) {
    return (
      <Button className={`${className} flex items-center gap-2`} disabled>
        <Plus className="h-4 w-4" />
        Enter Transaction
        <ChevronDown className="h-4 w-4" />
      </Button>
    );
  }

  // Determine which types to display
  const displayTypes = filteredTransactionTypes.length > 0 ? filteredTransactionTypes : allTransactionTypes;

  // Sort to show green (Sales) items first, then orange (Purchase) items
  const sortedDisplayTypes = displayTypes.sort((a, b) => {
    if (a.Typ === 'S' && b.Typ !== 'S') return -1;
    if (a.Typ !== 'S' && b.Typ === 'S') return 1;
    if (a.Typ === 'P' && b.Typ !== 'P') return -1;
    if (a.Typ !== 'P' && b.Typ === 'P') return 1;
    return 0;
  });

  // Show disabled button if no types to display
  if (displayTypes.length === 0) {
    return (
      <Button className={`${className} flex items-center gap-2`} disabled>
        <Plus className="h-4 w-4" />
        {title}
        <ChevronDown className="h-4 w-4" />
      </Button>
    );
  }



  // Handle mobile/desktop selection
  const handleSelect = (selectedValue, option) => {
    if (onTransactionTypeSelect) {
      onTransactionTypeSelect(selectedValue);
    }
    setIsOpen(false);
  };




  // Mobile view - use MobileFullScreenDropdown
  if (isMobile) {
    return (
      <MobileFullScreenDropdown
        options={sortedDisplayTypes}
        value={null}
        onSelect={(selectedValue, selectedOption) => {
          handleSelect(selectedValue, selectedOption);
        }}
        placeholder="Select Transaction Type"
        displayKey="XnTID"
        valueKey="XnTID"
        className={className}
        label="Transaction Type"
        emptyMessage="No transaction types available"
        searchPlaceholder="Search transaction types..."
        isIcon={minimalIcon}
        showButtonStyle={!minimalIcon}
        buttonTitle={title}
        showClearButton={false}
        showAllOption={false}
        searchThreshold={999}
        isSorting={false}
        mobileDropdownText="Select Transaction Type"
        externalOpen={externalOpen}
        onExternalOpenChange={onExternalOpenChange}
        renderOption={(option, isSelected) => {
          const IconComponent = getTransactionIcon(option);
          const colorClass = getTransactionIconColorClass(option);
          const displayName = getDisplayName(option);
          return (
            <div className="flex items-center gap-3 w-full">
              <div className={`w-5 h-5 flex items-center justify-center ${colorClass || 'text-gray-500'}`}>
                {/* Safely render the icon component */}
                {IconComponent ? (
                  typeof IconComponent === 'function' ? (
                    <IconComponent className="w-4 h-4" />
                  ) : (
                    IconComponent
                  )
                ) : (
                  // Fallback icon if getTransactionIcon returns null/undefined
                  <div className="w-4 h-4 bg-gray-300 rounded-sm flex items-center justify-center text-xs font-bold text-white">
                    {option.Typ || 'T'}
                  </div>
                )}
              </div>
              <span className="font-medium text-gray-900">
                {displayName}
              </span>
            </div>
          );
        }}
      />
    );
  }

  // Desktop view - use existing dropdown implementation
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {minimalIcon ? (
          <Plus
            className={`h-6 w-6 text-gray-600 hover:text-gray-800 cursor-pointer transition-colors ${className}`}
            data-transaction-dropdown-trigger
          />
        ) : (
          <Button
            className={`${className} flex items-center gap-2`}
            data-transaction-dropdown-trigger
          >
            <Plus className="h-4 w-4" />
            {title}
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-50 min-w-0 bg-white border border-gray-200 shadow-lg rounded-md"
        align={dropdownAlign}
      >
        {sortedDisplayTypes.map((transactionType) => (
          <DropdownMenuItem
            key={transactionType.XnTID}
            onClick={() => {
              if (onTransactionTypeSelect) {
                onTransactionTypeSelect(transactionType.XnTID);
              }
              setIsOpen(false);
            }}
            className="flex items-center gap-2 px-3 py-2 text-base cursor-pointer hover:bg-gray-50 focus:bg-gray-50 bg-white border-0"
          >
            <div className={getTransactionIconColorClass(transactionType)}>
              {getTransactionIcon(transactionType)}
            </div>
            <span className="font-medium text-gray-900">
              {getDisplayName(transactionType)}
            </span>
          </DropdownMenuItem>
        ))}

        {filteredTransactionTypes.length === 0 && connectionType && displayTypes.length > 0 && (
          <div className="px-3 py-2 text-xs text-gray-500 border-t">
            Showing all types (no {connectionType === 'C' ? 'sales' : 'purchase'} transactions found)
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TransactionDropdown;